import { useEffect, useRef, useState } from "react";
import { GiChemicalDrop } from "react-icons/gi";
import { FiAlertCircle, FiDollarSign, FiFilter, FiHash, FiMap, FiTrendingUp, FiX } from "react-icons/fi";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioAplicacaoInsumos = () => {
  const [dados, setDados] = useState({
    resumo: { total_aplicacoes: 0, total_area_aplicada: 0, custo_total: 0, custo_medio_hectare: 0 },
    aplicacoes: [],
    por_talhao: [],
    por_cultura: [],
    por_produto: [],
    comparativo: { por_produto: [], por_talhao: [], por_periodo: [] },
  });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("aplicacoes");

  // Opções para multi-selects (apenas campos com FK reais no modelo)
  const [culturas, setCulturas] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [fazendas, setFazendas] = useState([]);
  const [safras, setSafras] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    culturas: useRef(null),
    talhoes: useRef(null),
    fazendas: useRef(null),
    safras: useRef(null),
  };

  const [filtros, setFiltros] = useState({
    culturas: [],
    talhoes: [],
    fazendas: [],
    safras: [],
    // insumo_nome, operador_nome e equipamento_nome são CharField no modelo,
    // não FKs — filtro por texto livre
    insumo: "",
    operador: "",
    equipamento: "",
    data_inicio: "",
    data_fim: "",
    tipo_insumo: "todos",
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        dropdownRefs[openDropdown]?.current &&
        !dropdownRefs[openDropdown].current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [culturasRes, talhoesRes, fazendasRes, safrasRes] = await Promise.all([
        api.get("/agricultura/culturas/"),
        api.get("/fazendas/talhoes/"),
        api.get("/fazendas/fazendas/"),
        api.get("/agricultura/safras/"),
      ]);
      const extrair = (res) => {
        const d = res.data;
        return Array.isArray(d) ? d : d?.results || [];
      };
      setCulturas(extrair(culturasRes));
      setTalhoes(extrair(talhoesRes));
      setFazendas(extrair(fazendasRes));
      setSafras(extrair(safrasRes));
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros((prev) => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options) && options.length > 0) {
      setFiltros((prev) => ({ ...prev, [campo]: options.map((opt) => opt.id) }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros((prev) => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");
    try {
      const params = {};
      if (filtros.culturas?.length > 0) params.culturas = filtros.culturas.join(",");
      if (filtros.talhoes?.length > 0) params.talhoes = filtros.talhoes.join(",");
      if (filtros.fazendas?.length > 0) params.fazendas = filtros.fazendas.join(",");
      if (filtros.safras?.length > 0) params.safras = filtros.safras.join(",");
      if (filtros.insumo?.trim()) params.insumo = filtros.insumo.trim();
      if (filtros.operador?.trim()) params.operador = filtros.operador.trim();
      if (filtros.equipamento?.trim()) params.equipamento = filtros.equipamento.trim();
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.tipo_insumo && filtros.tipo_insumo !== "todos") params.tipo_insumo = filtros.tipo_insumo;

      const response = await api.get("/relatorios/agricolas/aplicacao-insumos/", { params });

      setDados(
        response.data || {
          resumo: { total_aplicacoes: 0, total_area_aplicada: 0, custo_total: 0, custo_medio_hectare: 0 },
          aplicacoes: [],
          por_talhao: [],
          por_cultura: [],
          por_produto: [],
          comparativo: { por_produto: [], por_talhao: [], por_periodo: [] },
        }
      );
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório de aplicação de insumos. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      culturas: [], talhoes: [], fazendas: [], safras: [],
      insumo: "", operador: "", equipamento: "",
      data_inicio: "", data_fim: "", tipo_insumo: "todos",
    });
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);
  };

  // Evita problema de fuso horário do new Date("YYYY-MM-DD")
  const formatarData = (data) => {
    if (!data) return "-";
    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    }
    return data;
  };

  const formatarDose = (dose, unidade) => {
    if (!dose && dose !== 0) return "-";
    return `${dose} ${unidade || "L/ha"}`;
  };

  // ─── EXPORTAR EXCEL ───────────────────────────────────────────────────────────
  const exportarExcel = () => {
    const aplicacoes = dados?.aplicacoes || [];
    if (aplicacoes.length === 0) {
      alert("Não há dados para exportar!");
      return;
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(aplicacoes), "Aplicações");
    if (dados?.por_talhao?.length > 0)
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dados.por_talhao), "Por Talhão");
    if (dados?.por_produto?.length > 0)
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dados.por_produto), "Por Produto");
    XLSX.writeFile(workbook, "relatorio_aplicacao_insumos.xlsx");
  };

  // ─── EXPORTAR PDF ────────────────────────────────────────────────────────────
  const exportarPDF = async () => {
    await exportarRelatorioPDF({
      titulo: "Relatório de Aplicação de Insumos",
      colunas: [
        { key: "data", label: "DATA" },
        { key: "talhao", label: "TALHÃO" },
        { key: "cultura", label: "CULTURA" },
        { key: "produto", label: "PRODUTO" },
        { key: "dose", label: "DOSE" },
        { key: "area_aplicada", label: "ÁREA (ha)" },
        { key: "operador", label: "OPERADOR" },
        { key: "custo_total", label: "CUSTO" },
      ],
      dados: dados?.aplicacoes || [],
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      columnStyles: { 0: { cellWidth: 22, halign: "center" } },
      formatarValor: (valor, coluna, item) => {
        if (coluna.key === "dose") return formatarDose(item.dose, item.unidade_dose);
        if (coluna.key === "area_aplicada") return `${valor || 0} ha`;
        if (coluna.key === "custo_total") return formatarMoeda(valor);
        return valor ?? "-";
      },
    });
  };

  // ─── IMPRIMIR ─────────────────────────────────────────────────────────────────
  const imprimir = () => {
    const aplicacoes = dados?.aplicacoes || [];
    if (aplicacoes.length === 0) {
      alert("Não há dados para imprimir!");
      return;
    }
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatório de Aplicação de Insumos</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            .resumo-cards { display: flex; gap: 15px; margin-bottom: 25px; flex-wrap: wrap; }
            .card { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; min-width: 140px; }
            .card .valor { font-size: 22px; font-weight: bold; margin-top: 5px; }
            .card .label { font-size: 11px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print { body { margin: 0; padding: 15px; } }
          </style>
        </head>
        <body>
          <h1>Relatório de Aplicação de Insumos</h1>
          <p>Gerado em: ${new Date().toLocaleString("pt-BR")}</p>
          <div class="resumo-cards">
            <div class="card"><div class="label">Total Aplicações</div><div class="valor">${dados?.resumo?.total_aplicacoes || 0}</div></div>
            <div class="card"><div class="label">Área Aplicada</div><div class="valor">${Number(dados?.resumo?.total_area_aplicada || 0).toFixed(2)} ha</div></div>
            <div class="card"><div class="label">Custo Total</div><div class="valor">${formatarMoeda(dados?.resumo?.custo_total)}</div></div>
            <div class="card"><div class="label">Custo Médio/ha</div><div class="valor">${formatarMoeda(dados?.resumo?.custo_medio_hectare)}</div></div>
          </div>
          <table>
            <thead>
              <tr><th>DATA</th><th>TALHÃO</th><th>CULTURA</th><th>PRODUTO</th><th>DOSE</th><th>ÁREA</th><th>OPERADOR</th><th>CUSTO</th></tr>
            </thead>
            <tbody>
              ${aplicacoes
                .map(
                  (item) => `
                <tr>
                  <td>${item.data || "-"}</td>
                  <td>${item.talhao || "-"}</td>
                  <td>${item.cultura || "-"}</td>
                  <td>${item.produto || "-"}</td>
                  <td>${formatarDose(item.dose, item.unidade_dose)}</td>
                  <td>${item.area_aplicada || 0} ha</td>
                  <td>${item.operador || "-"}</td>
                  <td>${formatarMoeda(item.custo_total)}</td>
                </tr>`
                )
                .join("")}
            </tbody>
          </table>
          <div class="footer">Sistema de Gestão Agropecuária</div>
          <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); }<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ─── COMPONENTE MULTI-SELECT ─────────────────────────────────────────────────
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter((opt) => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map((s) => s.nome || s.nome_codigo).join(", ");
      return `${selected.length} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
        <div
          onClick={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className="text-gray-700 truncate">{getSelectedNames()}</span>
          <span className="ml-2 flex-shrink-0 text-gray-400">▼</span>
        </div>
        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50 flex gap-3">
              <button onClick={() => handleClearAll(fieldName)} className="text-sm text-blue-600 hover:text-blue-800">
                Limpar
              </button>
              <button onClick={() => handleSelectAll(fieldName, optionsArray)} className="text-sm text-blue-600 hover:text-blue-800">
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">Nenhuma opção disponível</p>
            ) : (
              optionsArray.map((option) => (
                <label key={option.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-gray-700">{option.nome || option.nome_codigo}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  const inputCls =
    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition";

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={GiChemicalDrop}
            titulo="Relatório de Aplicação de Insumos"
            descricao="Consulte o histórico de aplicações de fertilizantes, defensivos e outros insumos agrícolas"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Multi-selects — campos com FK reais */}
              <MultiSelect
                label="Culturas"
                options={culturas}
                selectedValues={filtros.culturas}
                fieldName="culturas"
                refProp={dropdownRefs.culturas}
              />
              <MultiSelect
                label="Talhões"
                options={talhoes}
                selectedValues={filtros.talhoes}
                fieldName="talhoes"
                refProp={dropdownRefs.talhoes}
              />
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />
              <MultiSelect
                label="Safras"
                options={safras}
                selectedValues={filtros.safras}
                fieldName="safras"
                refProp={dropdownRefs.safras}
              />

              {/* Inputs de texto — campos CharField no modelo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Insumo</label>
                <input
                  name="insumo"
                  placeholder="Nome do insumo..."
                  value={filtros.insumo}
                  onChange={handleChange}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Operador</label>
                <input
                  name="operador"
                  placeholder="Nome do operador..."
                  value={filtros.operador}
                  onChange={handleChange}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Equipamento</label>
                <input
                  name="equipamento"
                  placeholder="Nome do equipamento..."
                  value={filtros.equipamento}
                  onChange={handleChange}
                  className={inputCls}
                />
              </div>

              {/* Tipo de insumo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Insumo</label>
                <select name="tipo_insumo" value={filtros.tipo_insumo} onChange={handleChange} className={inputCls + " bg-white"}>
                  <option value="todos">Todos</option>
                  <option value="fertilizante">Fertilizantes</option>
                  <option value="defensivo">Defensivos</option>
                  <option value="semente">Sementes</option>
                  <option value="corretivo">Corretivos</option>
                  <option value="outro">Outros</option>
                </select>
              </div>

              {/* Período */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data Início</label>
                <input type="date" name="data_inicio" value={filtros.data_inicio} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data Fim</label>
                <input type="date" name="data_fim" value={filtros.data_fim} onChange={handleChange} className={inputCls} />
              </div>
            </div>

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal de resultados */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", overflow: "auto" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{ maxHeight: "calc(100vh - 4rem)", zIndex: 10000 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <GiChemicalDrop size={22} className="text-brand-green" />
                Relatório de Aplicação de Insumos
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
              >
                <FiX size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Abas */}
            <div className="border-b border-gray-200 px-6 flex-shrink-0">
              <div className="flex gap-4 overflow-x-auto">
                {[
                  { id: "aplicacoes", label: "Aplicações" },
                  { id: "talhoes", label: "Por Talhão" },
                  { id: "comparativo", label: "Comparativo" },
                ].map((aba) => (
                  <button
                    key={aba.id}
                    onClick={() => setAbaAtiva(aba.id)}
                    className={`py-3 px-4 font-medium whitespace-nowrap border-b-2 transition ${
                      abaAtiva === aba.id
                        ? "border-brand-green text-brand-green"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {aba.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Ações */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Conteúdo das abas */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              {/* ABA: Aplicações */}
              {abaAtiva === "aplicacoes" && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <CartaoEstatistica
                      icone={FiHash}
                      rotulo="Total de Aplicações"
                      valor={dados?.resumo?.total_aplicacoes || 0}
                      cor="azul"
                    />
                    <CartaoEstatistica
                      icone={FiMap}
                      rotulo="Área Aplicada"
                      valor={`${Number(dados?.resumo?.total_area_aplicada || 0).toFixed(1)} ha`}
                      cor="verde"
                    />
                    <CartaoEstatistica
                      icone={FiDollarSign}
                      rotulo="Custo Total"
                      valor={formatarMoeda(dados?.resumo?.custo_total)}
                      cor="roxo"
                    />
                    <CartaoEstatistica
                      icone={FiTrendingUp}
                      rotulo="Custo Médio/ha"
                      valor={`${formatarMoeda(dados?.resumo?.custo_medio_hectare)}/ha`}
                      cor="dourado"
                    />
                  </div>

                  {(dados?.aplicacoes || []).length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Data</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Talhão</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cultura</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Produto</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Dose</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Área (ha)</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Operador</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Custo Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados?.aplicacoes || []).map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{item.data || "-"}</td>
                              <td className="px-4 py-3 text-sm">{item.talhao || "-"}</td>
                              <td className="px-4 py-3 text-sm">{item.cultura || "-"}</td>
                              <td className="px-4 py-3 text-sm">{item.produto || "-"}</td>
                              <td className="px-4 py-3 text-sm">{formatarDose(item.dose, item.unidade_dose)}</td>
                              <td className="px-4 py-3 text-sm">{item.area_aplicada || 0}</td>
                              <td className="px-4 py-3 text-sm">{item.operador || "-"}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-purple-600">
                                {formatarMoeda(item.custo_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
                  )}
                </div>
              )}

              {/* ABA: Por Talhão */}
              {abaAtiva === "talhoes" && (
                <div>
                  {(dados?.por_talhao || []).length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Talhão</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cultura</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Aplicações</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Área Total (ha)</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Custo Total</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Custo/ha</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Última Aplicação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados?.por_talhao || []).map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{item.talhao || "-"}</td>
                              <td className="px-4 py-3 text-sm">{item.cultura || "-"}</td>
                              <td className="px-4 py-3 text-sm">{item.numero_aplicacoes || 0}</td>
                              <td className="px-4 py-3 text-sm">{Number(item.area_total || 0).toFixed(2)}</td>
                              <td className="px-4 py-3 text-sm text-purple-600">{formatarMoeda(item.custo_total)}</td>
                              <td className="px-4 py-3 text-sm">{formatarMoeda(item.custo_por_hectare)}/ha</td>
                              <td className="px-4 py-3 text-sm">{item.ultima_aplicacao || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Nenhum dado por talhão disponível." />
                  )}
                </div>
              )}

              {/* ABA: Comparativo */}
              {abaAtiva === "comparativo" && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Comparativo por Produto</h4>
                  {(dados?.comparativo?.por_produto || []).length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Produto</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Qtd. Utilizada</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Área Total (ha)</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Custo Total</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Custo/ha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados?.comparativo?.por_produto || []).map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{item.produto || "-"}</td>
                              <td className="px-4 py-3 text-sm capitalize">{item.tipo || "-"}</td>
                              <td className="px-4 py-3 text-sm">
                                {Number(item.quantidade_total || 0).toFixed(2)} {item.unidade || ""}
                              </td>
                              <td className="px-4 py-3 text-sm">{Number(item.area_total || 0).toFixed(2)}</td>
                              <td className="px-4 py-3 text-sm text-purple-600">{formatarMoeda(item.custo_total)}</td>
                              <td className="px-4 py-3 text-sm">{formatarMoeda(item.custo_por_hectare)}/ha</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Nenhum dado comparativo disponível." />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
              <span className="text-sm text-gray-500">
                Total: <strong>{(dados?.aplicacoes || []).length}</strong> aplicação(ões)
              </span>
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioAplicacaoInsumos;
